// Volume pricing popup logic.
// - Reads base price from trigger data attributes (in cents).
// - Uses theme.volumePricingConfig for quantity and discount tiers.
// - Renders a per-item pricing grid into the global popup.

(function () {
  if (!window.theme || !theme.volumePricingConfig) return;

  const config = theme.volumePricingConfig;
  const modal = document.getElementById('volume-pricing-modal');

  if (!modal) return;

  const rowsContainer = modal.querySelector('[data-volume-pricing-rows]');

  /**
   * Safely parses an integer from a data attribute.
   * @param {string|null} value
   * @returns {number|null}
   */
  function parseCents(value) {
    if (value == null || value === '') return null;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  /**
   * Determines the discount percent for a given subtotal (in cents).
   * @param {number} subtotalCents
   * @returns {number} discountPercent (0–1)
   */
  function getDiscountPercent(subtotalCents) {
    let percent = 0;

    config.orderDiscountTiers.forEach((tier) => {
      const meetsMin = subtotalCents >= tier.minSubtotalCents;
      const underMax = tier.maxSubtotalCents == null || subtotalCents <= tier.maxSubtotalCents;

      if (meetsMin && underMax) {
        percent = tier.discountPercent;
      }
    });

    return percent;
  }

  /**
   * Formats cents into a money string with $ (e.g. $20.00).
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    const safeCents = Number.isFinite(cents) ? cents : 0;

    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      const moneyFormat = '${{amount}}';
      return Shopify.formatMoney(safeCents, moneyFormat);
    }

    return '$' + (safeCents / 100).toFixed(2);
  }

  /**
   * Builds rows for the volume pricing table based on order-value tiers:
   * - If 1 × price < $500: show a 0% row (No Minimums) + 2%, 5%, 10%, 15% tiers.
   * - If 1 × price is already in a discount tier, skip lower tiers and 0%.
   * @param {number} basePriceCents
   * @returns {Array<{ quantityLabel: string, perItemCents: number, discountPercent: number }>}
   */
  function buildTierRows(basePriceCents) {
    const tiers = config.orderDiscountTiers;
    if (!basePriceCents || !tiers.length) return [];

    const threshold500 = tiers[0].minSubtotalCents;   // 50000
    const threshold1000 = tiers[1].minSubtotalCents;  // 100000
    const threshold2000 = tiers[2].minSubtotalCents;  // 200000
    const threshold5000 = tiers[3].minSubtotalCents;  // 500000

    // Determine what discount tier already applies at quantity 1.
    const baseDiscount = getDiscountPercent(basePriceCents * 1);

    const rows = [];

    // Row 1: only when there is a true 0% tier (subtotal < $500).
    if (baseDiscount === 0) {
      const maxQtyBelow500 = Math.floor((threshold500 - 1) / basePriceCents);
      const qtyLabel1 = maxQtyBelow500 > 1
        ? `1 - ${maxQtyBelow500}`
        : '1';

      rows.push({
        quantityLabel: qtyLabel1,
        perItemCents: basePriceCents,
        discountPercent: 0
      });
    }

    // Tier quantities at thresholds.
    const qty2 = Math.ceil(threshold500 / basePriceCents);
    const subtotal2 = basePriceCents * qty2;
    const perItem2 = Math.round((subtotal2 * (1 - tiers[0].discountPercent)) / qty2);

    // Row 3: min quantity for >= $1000
    const qty3 = Math.ceil(threshold1000 / basePriceCents);
    const subtotal3 = basePriceCents * qty3;
    const perItem3 = Math.round((subtotal3 * (1 - tiers[1].discountPercent)) / qty3);

    // Row 4: min quantity for >= $2000
    const qty4 = Math.ceil(threshold2000 / basePriceCents);
    const subtotal4 = basePriceCents * qty4;
    const perItem4 = Math.round((subtotal4 * (1 - tiers[2].discountPercent)) / qty4);

    // Row 5: min quantity for >= $5000, show "qty+"
    const qty5 = Math.ceil(threshold5000 / basePriceCents);
    const subtotal5 = basePriceCents * qty5;
    const perItem5 = Math.round((subtotal5 * (1 - tiers[3].discountPercent)) / qty5);

    // Helper to add a tier row only if its discount is >= the base tier.
    function addTierRow(tierIndex, qty, perItem, labelSuffix = '') {
      const tier = tiers[tierIndex];
      if (tier.discountPercent < baseDiscount) return;

      rows.push({
        quantityLabel: String(qty) + labelSuffix,
        perItemCents: perItem,
        discountPercent: tier.discountPercent
      });
    }

    // 2% tier (500–999)
    addTierRow(0, qty2, perItem2);
    // 5% tier (1,000–1,999)
    addTierRow(1, qty3, perItem3);
    // 10% tier (2,000–4,999)
    addTierRow(2, qty4, perItem4);
    // 15% tier (5,000+), label as "qty+"
    addTierRow(3, qty5, perItem5, '+');

    return rows;
  }

  /**
   * Renders the pricing rows plus a final row: "Buying higher quantities? Contact us".
   * @param {number} basePriceCents
   */
  function renderPricingRows(basePriceCents) {
    if (!rowsContainer) return;

    rowsContainer.innerHTML = '';

    const rows = buildTierRows(basePriceCents);

    rows.forEach(({ quantityLabel, perItemCents, discountPercent }) => {
      const row = document.createElement('tr');

      const quantityCell = document.createElement('td');
      quantityCell.textContent = quantityLabel;

      const priceCell = document.createElement('td');
      const priceText = formatMoney(perItemCents);

      if (discountPercent > 0) {
        const percentageLabel = `${Math.round(discountPercent * 100)}% savings`;
        priceCell.innerHTML = `<span>${priceText}</span> <span class="small-text volume-pricing-modal__discount">${percentageLabel}</span>`;
      } else {
        priceCell.textContent = priceText;
      }

      row.appendChild(quantityCell);
      row.appendChild(priceCell);
      rowsContainer.appendChild(row);
    });

    // 5th row: "Buying higher quantities? Contact us" (Contact us opens Shopify Chat)
    const contactRow = document.createElement('tr');
    const contactCell = document.createElement('td');
    contactCell.setAttribute('colspan', '2');
    contactCell.className = 'volume-pricing-modal__contact-cell';
    contactCell.innerHTML = 'Buying higher quantities? <a href="#" class="volume-pricing-modal__contact-link">Contact us</a>';
    contactRow.appendChild(contactCell);
    rowsContainer.appendChild(contactRow);
  }

  // Delegate click on "Contact us" to open Shopify Chat (reusable via theme.openShopifyChat)
  modal.addEventListener('click', function(evt) {
    if (evt.target.classList.contains('volume-pricing-modal__contact-link')) {
      evt.preventDefault();
      if (typeof theme !== 'undefined' && typeof theme.openShopifyChat === 'function') {
        theme.openShopifyChat();
      }
    }
  });

  /**
   * Opens the modal for a given trigger.
   * @param {HTMLElement} trigger
   */
  function openVolumePricingModal(trigger) {
    const priceCents = parseCents(trigger.getAttribute('data-volume-price'));
    const compareAtCents = parseCents(trigger.getAttribute('data-volume-compare-price')) || 0;

    if (priceCents == null) return;

    // Base price logic: effective selling price is always variant.price.
    const basePriceCents = priceCents;

    renderPricingRows(basePriceCents);

    const openModal = () => {
      if (typeof modal.open === 'function') {
        modal.open(trigger);
      } else {
        modal.setAttribute('open', '');
      }

      // Position the popup close to the trigger, similar to a tooltip.
      const windowEl = modal.querySelector('.modal__window--volume-pricing');
      if (windowEl) {
        const rect = trigger.getBoundingClientRect();
        const margin = 8;

        const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
        const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
        const popupWidth = windowEl.offsetWidth || 320;
        const popupHeight = windowEl.offsetHeight || 220;

        // Try to place below the trigger; if it would be cut off, place above.
        let top = rect.bottom + margin;
        if (top + popupHeight + margin > viewportHeight) {
          top = rect.top - popupHeight - margin;
        }
        if (top < margin) {
          top = margin;
        }

        // Horizontally, roughly align with trigger, clamped to viewport.
        let left = rect.left;

        if (left + popupWidth + margin > viewportWidth) {
          left = viewportWidth - popupWidth - margin;
        }
        if (left < margin) {
          left = margin;
        }

        windowEl.style.position = 'fixed';
        windowEl.style.top = `${top}px`;
        windowEl.style.left = `${left}px`;
        windowEl.style.transform = 'none';
        windowEl.style.margin = '0';
      }
    };

    if (typeof modal.open === 'function') {
      openModal();
    } else if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('modal-dialog').then(openModal);
    } else {
      openModal();
    }
  }

  /**
   * Uses event delegation so triggers still work after variant-picker DOM swaps.
   */
  function initVolumePricingTriggers() {
    document.addEventListener('click', (evt) => {
      const trigger = evt.target.closest('[data-volume-pricing-trigger]');
      if (!trigger) return;

      evt.preventDefault();
      evt.stopPropagation();
      openVolumePricingModal(trigger);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVolumePricingTriggers);
  } else {
    initVolumePricingTriggers();
  }
}());